const stateKey = "__solOfficeNavigation";
const beforeNavigate = "sol:before-navigate";
const routeChanged = "sol:route-changed";

type Entry = {
  version: 1;
  documentId: string;
  index: number;
  entryId: string;
};
type Snapshot = { entry: Entry; url: string; state: unknown };

function readEntry(state: unknown): Entry | null {
  if (!state || typeof state !== "object") return null;
  const value = (state as Record<string, unknown>)[stateKey];
  if (!value || typeof value !== "object") return null;
  const entry = value as Partial<Entry>;
  return entry.version === 1 &&
    typeof entry.documentId === "string" &&
    entry.documentId.length > 0 &&
    typeof entry.entryId === "string" &&
    entry.entryId.length > 0 &&
    Number.isSafeInteger(entry.index) &&
    entry.index! >= 0
    ? (entry as Entry)
    : null;
}

function freshEntry(): Entry {
  return {
    version: 1,
    documentId: crypto.randomUUID(),
    index: 0,
    entryId: crypto.randomUUID(),
  };
}

function withEntry(state: unknown, entry: Entry) {
  // Current app callers use object state. Retain an older non-object payload
  // too, without allowing it to overwrite the reserved navigation metadata.
  return {
    ...(state && typeof state === "object" && !Array.isArray(state)
      ? state
      : state == null
        ? {}
        : { __solOfficeHistoryPayload: state }),
    [stateKey]: entry,
  };
}

function sameEntry(left: Entry | null, right: Entry) {
  return (
    left?.documentId === right.documentId &&
    left.index === right.index &&
    left.entryId === right.entryId
  );
}

/** One controller survives the root layout effect's StrictMode remount. */
export function createNativeHistory({
  onChange,
  onRecovery,
}: {
  onChange: () => void;
  onRecovery: (message: string) => void;
}) {
  let accepted: Snapshot | null = null;
  let restoring = false;
  let stop: (() => void) | null = null;

  return {
    install() {
      // A second installation must never stack wrappers or route listeners.
      stop?.();
      const browserHistory = window.history;
      const originalPush = browserHistory.pushState;
      const originalReplace = browserHistory.replaceState;
      const originalGo = browserHistory.go;
      let active = true;
      let restoreTimer: ReturnType<typeof setTimeout> | undefined;

      const current = (): Snapshot => {
        let state: unknown = browserHistory.state;
        let entry = readEntry(state);
        if (!entry) {
          entry = freshEntry();
          state = withEntry(state, entry);
          originalReplace.call(browserHistory, state, "", location.href);
        }
        return { entry, state, url: location.href };
      };
      if (!accepted) accepted = current();

      const announce = () => window.dispatchEvent(new Event(routeChanged));
      const recoverUnindexed = () => {
        // The History API does not expose the distance to a foreign/unindexed
        // entry. Do not guess a direction or push another copy of the route.
        // Keep the mounted draft and replace only this transient entry, starting
        // a new lineage so future index arithmetic cannot cross that boundary.
        const entry = freshEntry();
        const state = withEntry(accepted!.state, entry);
        originalReplace.call(browserHistory, state, "", accepted!.url);
        accepted = { entry, state, url: location.href };
        restoring = false;
        onRecovery(
          "Your unsaved changes are still open. This older browser history could not be restored. Save your changes before continuing.",
        );
        announce();
      };

      const returnToAccepted = () => {
        if (!active || !restoring) return;
        const entry = readEntry(browserHistory.state);
        if (
          sameEntry(entry, accepted!.entry) &&
          location.href === accepted!.url
        ) {
          restoring = false;
          announce();
          return;
        }
        if (
          !entry ||
          entry.documentId !== accepted!.entry.documentId ||
          entry.index === accepted!.entry.index
        ) {
          recoverUnindexed();
          return;
        }
        originalGo.call(browserHistory, accepted!.entry.index - entry.index);
      };
      const scheduleReturn = () => {
        // Coalesce intervening traversals and calculate from the actual entry
        // at execution time, not the position of an earlier popstate event.
        if (restoreTimer !== undefined) clearTimeout(restoreTimer);
        restoreTimer = setTimeout(() => {
          restoreTimer = undefined;
          returnToAccepted();
        }, 0);
      };

      const blockDuringReturn = (event: Event) => {
        if (!restoring) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      const change = (event: Event) => {
        if (restoring) {
          event.stopImmediatePropagation();
          return;
        }
        accepted = current();
        onChange();
      };
      const pop = (event: PopStateEvent) => {
        // Native window listeners run in registration order. This root layout
        // listener preflights every live draft before a route observer can
        // unmount it; the canonical route event replaces all later pop handlers.
        event.stopImmediatePropagation();
        if (restoring) {
          // A second Back/Forward can arrive before the corrective traversal.
          // Only the precise retained entry can complete restoration.
          scheduleReturn();
          return;
        }
        if (
          location.href === accepted!.url ||
          window.dispatchEvent(new Event(beforeNavigate, { cancelable: true }))
        ) {
          accepted = current();
          announce();
          return;
        }
        restoring = true;
        scheduleReturn();
      };

      const push: History["pushState"] = function (state, unused, url) {
        if (restoring) return;
        const previous = current();
        const entry: Entry = {
          ...previous.entry,
          index: previous.entry.index + 1,
          entryId: crypto.randomUUID(),
        };
        // Advance the accepted position only after the native write succeeds.
        originalPush.call(browserHistory, withEntry(state, entry), unused, url);
        accepted = current();
      };
      const replace: History["replaceState"] = function (state, unused, url) {
        if (restoring) return;
        const entry = current().entry;
        originalReplace.call(
          browserHistory,
          withEntry(state, entry),
          unused,
          url,
        );
        accepted = current();
      };

      browserHistory.pushState = push;
      browserHistory.replaceState = replace;
      window.addEventListener(beforeNavigate, blockDuringReturn);
      window.addEventListener("popstate", pop);
      window.addEventListener(routeChanged, change);
      if (restoring) scheduleReturn();

      const cleanup = () => {
        active = false;
        if (restoreTimer !== undefined) clearTimeout(restoreTimer);
        window.removeEventListener(beforeNavigate, blockDuringReturn);
        window.removeEventListener("popstate", pop);
        window.removeEventListener(routeChanged, change);
        if (browserHistory.pushState === push)
          browserHistory.pushState = originalPush;
        if (browserHistory.replaceState === replace)
          browserHistory.replaceState = originalReplace;
        if (stop === cleanup) stop = null;
      };
      stop = cleanup;
      return cleanup;
    },
  };
}
